import App from '@/App.vue'
import type { SsrViewport } from '@/app/resolveSsrViewport'
import cms from '@/plugins/cms.js'
import filters from '@/plugins/filters.js'
import {
  formatCurrency,
  setupTranslations,
  translate,
  install as translationPlugin
} from '@/plugins/i18n.js'
import lozad from '@/plugins/lozad.js'
import clientVuetify, { createAppVuetify } from '@/plugins/vuetify.js'
import clientRouter, { createAppRouter } from '@/router/index.js'
import SiteConfig from '@/services/SiteConfig'
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import { createHead, createServerHead, VueHeadMixin } from '@unhead/vue'
import { createPinia, type Pinia, type StateTree } from 'pinia'
import {
  createApp as createClientApp,
  createSSRApp,
  markRaw,
  reactive,
  type App as VueApp
} from 'vue'
import type { I18n } from 'vue-i18n'
import type { Router } from 'vue-router'

// Types for the store properties the pinia plugin below injects, colocated
// with the injection so they cannot drift apart (VIP-12038).
declare module 'pinia' {
  export interface PiniaCustomProperties {
    router: Router
    translate: (key: string, ...params: unknown[]) => string
    formatCurrency: (value: number) => string
  }
}

export interface CreateVicampoAppOptions {
  /**
   * Mount into server-rendered markup instead of cold-mounting. Only relevant
   * on the client — the server always renders through an SSR app.
   */
  hydrate?: boolean
  /**
   * Viewport guess seeding Vuetify's breakpoints (see resolveSsrViewport). The
   * client passes the value the server rendered with, read back from the SSR
   * payload, so the hydration pass matches the markup — see createAppVuetify.
   */
  ssrViewport?: SsrViewport
  /**
   * Serialized Pinia state from an SSR response. It must be handed to the
   * factory rather than assigned afterwards: Pinia only takes its hydration
   * path when `pinia.state.value[id]` exists *before* the store is created,
   * and the cms plugin creates stores during `app.use(cms)` below. Assigning
   * later silently drops their payload and leaves `store.field` and
   * `store.$state` reading different objects (VIP-12037).
   */
  initialState?: StateTree
  /** Router override; the server entry passes a fresh per-request instance. */
  router?: Router
}

export interface VicampoAppContext {
  app: VueApp
  router: Router
  pinia: Pinia
  head: ReturnType<typeof createHead>
  vuetify: ReturnType<typeof createAppVuetify>
  i18n: I18n
}

/**
 * Build a fully wired app instance — the shared core of the client and server
 * entries (VIP-12037). Everything stateful (app, router, pinia, head, vuetify)
 * is created per call on the server so no state can leak between requests;
 * the client passes through to the module singletons the rest of the codebase
 * imports directly.
 */
export const createVicampoApp = async (
  options: CreateVicampoAppOptions = {}
): Promise<VicampoAppContext> => {
  const ssr = import.meta.env.SSR

  const app = (ssr || options.hydrate ? createSSRApp : createClientApp)(App)
  const router = options.router ?? (ssr ? createAppRouter() : clientRouter)
  const pinia = createPinia()
  // Before any store exists — see CreateVicampoAppOptions.initialState.
  if (options.initialState) pinia.state.value = options.initialState
  const head = ssr ? createServerHead() : createHead()
  // The site's language on <html> (de-DE/de-AT/de-CH) — read lazily so the
  // server resolves it per request; the client patches document.documentElement
  head.push({ htmlAttrs: { lang: () => SiteConfig.lang } })
  // A hydrating client needs its own instance seeded with the server's
  // viewport; the singleton measures the real window and would mismatch. An
  // older cached page without the payload falls back to it rather than
  // breaking (VIP-12333).
  const vuetify =
    ssr || (options.hydrate && options.ssrViewport)
      ? createAppVuetify(options.ssrViewport)
      : clientVuetify
  const i18n = (await setupTranslations()) as I18n

  app.config.globalProperties.$globals = reactive({})

  pinia.use(({ store }) => {
    store.router = markRaw(router)
    store.translate = markRaw(translate)
    store.formatCurrency = markRaw(formatCurrency)
  })

  // Pinia must be installed before the cms plugin, whose install() resolves
  // stores against the active pinia.
  app.use(pinia)
  app.use(router)
  // unhead must be installed BEFORE vuetify. Vuetify's theme plugin looks for
  // `app._context.provides.usehead` in its own install(): when it finds a head
  // instance it registers the generated theme stylesheet (~60 kB of
  // `--v-theme-*` tokens) as a head entry, which @unhead/ssr then renders into
  // the server HTML. With no head instance yet it falls back to injecting a
  // <style> into document.head — browser-only, so the server render shipped
  // without any theme at all: white page instead of the themed background,
  // wrong colours everywhere, until hydration patched it in.
  app.use(head)
  app.use(vuetify)
  app.use(translationPlugin, i18n)
  app.use(lozad)
  app.use(filters)
  app.use(cms)
  app.use(autoAnimatePlugin)
  app.mixin(VueHeadMixin)

  return { app, router, pinia, head, vuetify, i18n }
}
